export type CollapsibleInstructionStep = {
    text: string,
    linkText: string,
    collapsibleSteps: string[],
    textAfter?: string,
};

export type InstructionStep = string | CollapsibleInstructionStep;

export type SpConfigInstructions = {
    manualInstructions: InstructionStep[],
    fileInstructions: InstructionStep[],
    docUrl: string,
    docLabel: string,
    pdfGuide?: {url: string, label: string},
};

const okta: SpConfigInstructions = {
    docUrl: 'https://help.okta.com/en-us/content/topics/apps/apps_app_integration_wizard_saml.htm',
    docLabel: 'View Okta SAML documentation',
    pdfGuide: {url: '/idp-instructions/SSO Configuration Guide - Okta.pdf', label: 'Download Okta SSO Configuration Guide'},
    manualInstructions: [
        'Log in to your Okta Admin Console.',
        {
            text: 'Go to Applications → Applications and select your SAML app. If you haven’t created an Application yet, click ',
            linkText: 'here',
            textAfter: '.',
            collapsibleSteps: [
                'Click Create App Integration.',
                'Select SAML 2.0.',
                'Name application “PitchBook Platform” (logo is optional).',
                'Complete app creation.',
            ],
        },
        'On the General tab, scroll to SAML Settings and click Edit.',
        'Click Next to reach the Configure SAML step.',
        'In the Single sign-on URL field, paste the ACS URL shown below.',
        'In the Audience URI (SP Entity ID) field, paste the Entity ID shown below.',
        'Click Next, then Finish to save your changes.',
    ],
    fileInstructions: [
        'Log in to your Okta Admin Console.',
        'Go to Applications → Applications and select your SAML app. If a SAML app has not yet been created, click Create App Integration, select SAML 2.0, name the application "PitchBook Platform" (logo is optional), and complete the app creation before proceeding.',
        'On the General tab, scroll to SAML Settings and click Edit.',
        'Click Next to reach the Configure SAML step.',
        'Okta does not support direct metadata file upload — open the downloaded XML file and copy the values into the appropriate fields.',
        'Click Next, then Finish to save your changes.',
    ],
};

const microsoft: SpConfigInstructions = {
    docUrl: 'https://learn.microsoft.com/en-us/entra/identity/enterprise-apps/add-application-portal-setup-sso',
    docLabel: 'View Microsoft Entra ID documentation',
    pdfGuide: {url: '/idp-instructions/SSO Configuration Guide - Microsoft Entra.pdf', label: 'Download Microsoft Entra SSO Configuration Guide'},
    manualInstructions: [
        'Log in to the Microsoft Entra Admin Center.',
        {
            text: 'Go to Identity → Applications → Enterprise applications and select your app. If you haven\'t created an application yet, click ',
            linkText: 'here',
            textAfter: '.',
            collapsibleSteps: [
                'In Microsoft Entra admin center, select Enterprise Applications on the lefthand side.',
                'At the top of the page, select New application.',
                'Then select Create your own application as PitchBook does not have a pre-made application in the gallery.',
                'Enter "PitchBook" in the name field.',
                'Under "What are you looking to do with your application?" select the third option: "Integrate any other application you don\'t find in the gallery (Non-gallery)".',
                'Click Create.',
            ],
        },
        'Click Single sign-on in the left menu, then select SAML.',
        'In the Basic SAML Configuration section, click Edit.',
        'Enter the Identifier (Entity ID) and Reply URL (Assertion Consumer Service URL) from below.',
        'Click Save.',
    ],
    fileInstructions: [
        'Log in to the Microsoft Entra Admin Center.',
        'Go to Identity → Applications → Enterprise applications and select your app.',
        'Click Single sign-on in the left menu, then select SAML.',
        'In the Basic SAML Configuration section, click Edit.',
        'Click Upload metadata file and select the downloaded XML file — the fields will fill in automatically.',
        'Click Save.',
    ],
};

const onelogin: SpConfigInstructions = {
    docUrl: 'https://developers.onelogin.com/saml',
    docLabel: 'View OneLogin SAML documentation',
    manualInstructions: [
        'Log in to your OneLogin Admin Portal.',
        'Go to Applications → Applications and select your SAML app.',
        'Open the Configuration tab.',
        'In the Audience (Entity ID) field, paste the Entity ID shown below.',
        'In the ACS (Consumer) URL field, paste the ACS URL shown below.',
        'Click Save.',
    ],
    fileInstructions: [
        'Log in to your OneLogin Admin Portal.',
        'Go to Applications → Applications and select your SAML app.',
        'Open the Configuration tab.',
        'OneLogin does not support direct metadata file upload — open the downloaded XML file and copy the Entity ID and ACS URL into the appropriate fields.',
        'Click Save.',
    ],
};

const google: SpConfigInstructions = {
    docUrl: 'https://support.google.com/a/answer/6087519',
    docLabel: 'View Google Workspace documentation',
    pdfGuide: {url: '/idp-instructions/SSO Configuration Guide - Google Workspaces.pdf', label: 'Download Google Workspaces SSO Configuration Guide'},
    manualInstructions: [
        'Log in to the Google Admin Console.',
        {
            text: 'Go to Apps → Web and mobile apps and select your custom SAML app. If you haven\'t created an app yet, click ',
            linkText: 'here',
            textAfter: '.',
            collapsibleSteps: [
                'In Google Workspace admin console, select the Apps menu on the lefthand side.',
                'Select Web and mobile apps.',
                'Click the Add App dropdown, then select "Add custom SAML app".',
                'Enter "PitchBook" as the App name.',
                'Scroll down and select Continue.',
                'Skip Identity Provider Details by scrolling down and selecting Continue.',
            ],
        },
        'Click Service provider details.',
        'In the Entity ID field, paste the Entity ID shown below.',
        'In the ACS URL field, paste the ACS URL shown below.',
        'Click Save.',
    ],
    fileInstructions: [
        'Log in to the Google Admin Console.',
        'Go to Apps → Web and mobile apps and select your custom SAML app.',
        'Click Service provider details.',
        'Google Workspace does not support direct metadata file upload — open the downloaded XML file and copy the Entity ID and ACS URL into the appropriate fields.',
        'Click Save.',
    ],
};

const pingfederate: SpConfigInstructions = {
    docUrl: 'https://docs.pingidentity.com/pingfederate/13.0/pf_pf_landing_page.html',
    docLabel: 'View PingFederate documentation',
    manualInstructions: [
        'Log in to the PingFederate Admin Console.',
        'Go to Applications → Integration → SP Connections and select your connection.',
        'Manually enter the Entity ID and ACS URL in the appropriate fields.',
        'Click Save.',
    ],
    fileInstructions: [
        'Log in to the PingFederate Admin Console.',
        'Go to Applications → Integration → SP Connections and select your connection.',
        'Click Import Metadata and upload the downloaded XML file.',
        'Review the automatically populated Entity ID and ACS URL.',
        'Click Save.',
    ],
};

const other: SpConfigInstructions = {
    docUrl: '',
    docLabel: '',
    manualInstructions: [
        'Log in to your Identity Provider\'s admin console.',
        'Open the SAML application or SSO configuration for PitchBook.',
        'Paste the Entity ID and ACS URL from below into the appropriate fields.',
        'Save your changes.',
    ],
    fileInstructions: [
        'Log in to your Identity Provider\'s admin console.',
        'Open the SAML application or SSO configuration for PitchBook.',
        'If your provider supports metadata file upload, upload the downloaded XML file.',
        'Otherwise, open the file and copy the Entity ID and ACS URL into the appropriate fields.',
        'Save your changes.',
    ],
};

export const SP_CONFIG_INSTRUCTIONS: Record<string, SpConfigInstructions> = {
    okta,
    microsoft,
    onelogin,
    google,
    pingfederate,
    other,
};
